// AddAdminManagement.js
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  Image,
  Button,
} from "@chakra-ui/react";
import CloseIconBlack from "../../../assets/images/product/closeIconBlack.svg";
import { useForm } from "react-hook-form";
import { userGroupRolesLinksCall } from "../../../api/userGroupRolesLinks";
import TreeCheckbox from "./treeCheckBox";
import toast from "react-hot-toast";
import SearchBox from "./searchBox";

const buildParentMap = (node, map = {}, parent = null) => {
  if (parent) map[node.code] = parent.code;
  if (node.child) {
    node.child.forEach((child) => buildParentMap(child, map, node));
  }
  return map;
};

const AddAdminManagement = ({ isOpen, onClose, data, defaultValues, onSubmit }) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: defaultValues || {},
  });

  const [tree, setTree] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const parentMap = useRef({});

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
      const initial = defaultValues?.group_role_code?.split(",") || [];
      setSelectedRoles(initial);

      userGroupRolesLinksCall.tree().then((response) => {
        setTree(response);
        parentMap.current = buildParentMap(response);
      });
    } else {
      setSelectedRoles([]);
    }
  }, [isOpen, defaultValues, reset]);

  useEffect(() => {
    const updated = new Set(selectedRoles);
    let changed = false;

    for (const [child, parent] of Object.entries(parentMap.current)) {
      const siblings = Object.entries(parentMap.current)
        .filter(([k, p]) => p === parent)
        .map(([k]) => k);

      const allSiblingsSelected = siblings.every((s) => updated.has(s));

      if (allSiblingsSelected && !updated.has(parent)) {
        updated.add(parent);
        changed = true;
      }

      if (!allSiblingsSelected && updated.has(parent)) {
        updated.delete(parent);
        changed = true;
      }
    }

    if (changed) {
      setSelectedRoles(Array.from(updated));
    }
  }, [selectedRoles]);

  const toggleRole = (code, children = []) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      const isParentSelected = next.has(code);
      const allChildrenSelected = children.every((child) => next.has(child));

      if (isParentSelected && allChildrenSelected) {
        next.delete(code);
        children.forEach((c) => next.delete(c));
      } else {
        next.add(code);
        children.forEach((c) => next.add(c));
      }

      return Array.from(next);
    });
  };

  const onchangeEmployee = (option) => {
    setValue("user_id", option);
  };

  const submitHandler = (data) => {
    if (!data?.user_id) return toast.error("Vui lòng chọn nhân viên");
    if (selectedRoles.length === 0) return toast.error("Vui lòng chọn quyền cần cấp");
    onSubmit({ ...data, group_role_code: Array.from(new Set(selectedRoles)) });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent backgroundColor="var(--color-main)" borderRadius="16px" h="625px" maxW="625px" overflowY="auto">
        <form onSubmit={handleSubmit(submitHandler)}>
          <Flex minH="625px" flexDirection="column">
            <Flex justifyContent="space-between" alignItems="center" p="25px 24px" borderBottom="1px solid var(--bg-line-employee)">
              <Text color="var(--color-header-employee)" fontSize="18px" fontWeight="600">
                {defaultValues ? "Sửa Admin" : "Admin mới"}
              </Text>
              <Box cursor="pointer" onClick={onClose}>
                <Image src={CloseIconBlack} />
              </Box>
            </Flex>

            <Box flex={1} w="545px" m="24px auto">
              <Flex gap="27px" mb="16px">
                <SearchBox
                  label="Nhân viên"
                  isSearchable
                  placeholder="Chọn nhân viên"
                  selectedMain={watch("user_id")}
                  onChange={onchangeEmployee}
                  initialSelectedEmp={defaultValues?.employeeObject || null}
                />
                {tree && (
                  <TreeCheckbox
                    node={tree}
                    selectedRoles={selectedRoles}
                    toggleRole={toggleRole}
                    defaultExpandedRoles={selectedRoles}
                  />
                )}
              </Flex>
            </Box>

            <Flex p="24px" justifyContent="flex-end">
              <Button type="submit" w="164px" h="44px" color="var(--color-main)" bg="var(--linear-gradient-employee)">
                {defaultValues ? "Sửa admin" : "Tạo admin"}
              </Button>
            </Flex>
          </Flex>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default AddAdminManagement;
